import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Users, Calendar, BarChart3, FileText, DollarSign, Ban, CheckCircle, Plus, X, AlertTriangle, Eye, EyeOff, Star, Wallet, Clock, XCircle, Ticket, ShoppingBag } from 'lucide-react';
import { adminAPI, fxAPI, payoutAPI, adminRolesAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { toast } from 'sonner';

const ROLE_OPTIONS = [
  { value: 'finance_admin', label: 'Finance Admin' },
  { value: 'translator_admin', label: 'Translator Admin' },
  { value: 'support_admin', label: 'Support Admin' },
];

export default function AdminPanel() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const { format } = useCurrency();
  const navigate = useNavigate();
  const [tab, setTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [logs, setLogs] = useState([]);
  const [fxRates, setFxRates] = useState(null);
  const [loading, setLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [requestFilter, setRequestFilter] = useState('');
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [adminForm, setAdminForm] = useState({ email: '', name: '', role: 'support_admin' });
  const [fxForm, setFxForm] = useState({ EUR: 1.0, PLN: 4.25, CHF: 0.93 });
  const [payoutRequests, setPayoutRequests] = useState([]);
  const [payoutFilter, setPayoutFilter] = useState('pending');

  // Role-based, matching the API's role:super_admin middleware.
  const isSuperAdmin = user?.role === 'super_admin';
  const currentUserId = user?.user_id || user?.userId;

  useEffect(() => {
    if (!authLoading && (!user || !['super_admin', 'finance_admin', 'translator_admin', 'support_admin'].includes(user.role))) {
      navigate('/auth');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (tab === 'dashboard') {
      setLoading(true);
      adminAPI.dashboard().then(r => setDashboard(r.data)).catch(() => {}).finally(() => setLoading(false));
    } else if (tab === 'users') {
      setLoading(true);
      adminAPI.users({ search: userSearch, request: requestFilter || undefined }).then(r => setUsers(r.data.data || [])).catch(() => {}).finally(() => setLoading(false));
    } else if (tab === 'events') {
      setLoading(true);
      adminAPI.events().then(r => setEvents(r.data.data || [])).catch(() => {}).finally(() => setLoading(false));
    } else if (tab === 'logs') {
      setLoading(true);
      adminAPI.auditLogs().then(r => setLogs(r.data.data || [])).catch(() => {}).finally(() => setLoading(false));
    } else if (tab === 'fx') {
      fxAPI.rates().then(r => {
        setFxRates(r.data);
        setFxForm(r.data.rates || {});
      }).catch(() => {});
    } else if (tab === 'payouts') {
      setLoading(true);
      payoutAPI.adminList(payoutFilter).then(r => setPayoutRequests(r.data.payout_requests || [])).catch(() => {}).finally(() => setLoading(false));
    }
  }, [tab, userSearch, requestFilter, payoutFilter]);

  const handleBanUser = async (userId, isBanned) => {
    if (!isSuperAdmin) { toast.error('Super admin only'); return; }
    try {
      if (isBanned) {
        await adminAPI.unbanUser(userId);
        toast.success('User unbanned');
      } else {
        await adminAPI.banUser(userId);
        toast.success('User banned');
      }
      setUsers(us => us.map(u => u.user_id === userId ? { ...u, is_banned: !isBanned } : u));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleToggleDj = async (userId, isDj) => {
    if (!isSuperAdmin) { toast.error('Super admin only'); return; }
    try {
      if (isDj) {
        await adminAPI.removeDj(userId);
        toast.success('DJ role removed');
      } else {
        await adminAPI.makeDj(userId);
        toast.success('User promoted to DJ');
      }
      setUsers(us => us.map(u => u.user_id === userId ? { ...u, is_dj: !isDj } : u));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleCapability = async (u, capability, action) => {
    if (!window.confirm(`${action === 'grant' ? 'Grant' : 'Remove'} ${capability} ${action === 'grant' ? 'to' : 'from'} ${u.email}?`)) return;
    try {
      const r = await adminRolesAPI.setCapability(u.user_id, capability, action);
      setUsers(us => us.map(x => x.user_id === u.user_id ? { ...x, ...r.data } : x));
      toast.success('Roles updated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleVerify = async (u) => {
    try {
      const r = await adminRolesAPI.setVerified(u.user_id, !u.verified_at);
      setUsers(us => us.map(x => x.user_id === u.user_id ? { ...x, ...r.data } : x));
      toast.success(u.verified_at ? 'Verification removed' : 'Verified');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    try {
      await adminAPI.createAdmin(adminForm);
      toast.success('Admin user created!');
      setShowCreateAdmin(false);
      if (tab === 'users') {
        const resp = await adminAPI.users({});
        setUsers(resp.data.users || []);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleTogglePublish = async (ev) => {
    try {
      await adminAPI.publishEvent(ev.event_id, !ev.is_published);
      setEvents(evs => evs.map(e => e.event_id === ev.event_id ? { ...e, is_published: !e.is_published } : e));
      toast.success(ev.is_published ? 'Unpublished' : 'Published');
    } catch { toast.error('Failed'); }
  };

  const handleToggleFeature = async (ev) => {
    try {
      await adminAPI.featureEvent(ev.event_id, !ev.is_featured);
      setEvents(evs => evs.map(e => e.event_id === ev.event_id ? { ...e, is_featured: !e.is_featured } : e));
      toast.success(ev.is_featured ? 'Unfeatured' : 'Featured!');
    } catch { toast.error('Failed'); }
  };

  const handleSaveFX = async () => {
    try {
      await fxAPI.update({ ...fxRates, rates: { ...fxForm } });
      toast.success('FX rates updated!');
    } catch { toast.error('Failed to update FX rates'); }
  };

  const TABS = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3, roles: ['super_admin', 'finance_admin', 'translator_admin', 'support_admin'] },
    { id: 'users', label: 'Users', icon: Users, roles: ['super_admin', 'support_admin'] },
    { id: 'events', label: 'Events', icon: Calendar, roles: ['super_admin', 'support_admin'] },
    { id: 'payouts', label: 'Payouts', icon: Wallet, roles: ['super_admin', 'finance_admin'] },
    { id: 'promoters', label: 'Promoters', icon: Star, roles: ['super_admin', 'finance_admin', 'support_admin'] },
    { id: 'logs', label: 'Audit Logs', icon: FileText, roles: ['super_admin'] },
    { id: 'fx', label: 'FX Management', icon: DollarSign, roles: ['super_admin', 'finance_admin'] },
  ].filter(tab => tab.roles.includes(user?.role));

  return (
    <div className="min-h-screen bg-[#050505] pt-16 page-enter" data-testid="admin-panel">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Shield size={28} className="text-yellow-400" />
          <div>
            <h1 className="text-3xl font-black uppercase tracking-tight text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              {t('adminPanel')}
            </h1>
            <p className="text-gray-500 text-sm">{user?.role?.replace('_', ' ').toUpperCase()}</p>
          </div>
          {isSuperAdmin && (
            <button onClick={() => setShowCreateAdmin(true)} className="ml-auto btn-primary px-4 py-2 rounded text-sm font-bold flex items-center gap-2" data-testid="create-admin-btn">
              <Plus size={14} /> Create Admin
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 mb-8 bg-[#0A0A0A] border border-gray-800 rounded-lg p-1 w-fit">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded text-xs font-bold uppercase transition-colors ${tab === id ? 'bg-red-600 text-white' : 'text-gray-500 hover:text-white'}`}
              data-testid={`admin-tab-${id}`}
            >
              <Icon size={12} /> {label}
            </button>
          ))}
        </div>

        {/* Dashboard tab */}
        {tab === 'dashboard' && dashboard && (
          <div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-8">
              {[
                { label: 'Total Users', value: dashboard.total_users ?? 0, color: 'text-blue-400', bg: 'bg-blue-500', icon: Users },
                { label: 'Total Events', value: dashboard.total_events ?? 0, color: 'text-purple-400', bg: 'bg-purple-500', icon: Calendar },
                { label: 'Paid Orders', value: dashboard.paid_orders ?? 0, color: 'text-green-400', bg: 'bg-green-500', icon: ShoppingBag },
                { label: 'Tickets Issued', value: dashboard.total_tickets ?? 0, color: 'text-red-400', bg: 'bg-red-500', icon: Ticket },
                { label: 'Total Revenue', value: format(dashboard.total_revenue), color: 'text-yellow-400', bg: 'bg-orange-500', icon: DollarSign },
              ].map(({ label, value, color, bg, icon: Icon }) => (
                <div key={label} className="bg-[#0A0A0A] border border-gray-800 rounded-xl p-5">
                  <div className={`${bg} w-9 h-9 rounded-lg flex items-center justify-center mb-3`}>
                    <Icon size={17} className="text-white" />
                  </div>
                  <p className={`${color} font-black text-2xl`}>{value}</p>
                  <p className="text-gray-500 text-xs uppercase tracking-wider mt-1">{label}</p>
                </div>
              ))}
            </div>
            {dashboard.recent_orders?.length > 0 && (
              <div className="bg-[#0A0A0A] border border-gray-800 rounded-xl p-6">
                <h2 className="text-white font-bold uppercase text-sm mb-4">Recent Orders</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-gray-500 text-xs uppercase border-b border-gray-800">
                        <th className="text-left pb-3">Order ID</th>
                        <th className="text-left pb-3">Event</th>
                        <th className="text-left pb-3">Amount</th>
                        <th className="text-left pb-3">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-900">
                      {dashboard.recent_orders.map(order => (
                        <tr key={order.order_id}>
                          <td className="py-3 font-mono text-yellow-400 text-xs">{order.order_id?.slice(0,8).toUpperCase()}</td>
                          <td className="py-3 text-white text-xs truncate max-w-32">{order.event_title}</td>
                          <td className="py-3 text-white font-bold">{format(order.total_amount)}</td>
                          <td className="py-3">
                            <span className={`text-xs px-2 py-0.5 rounded font-bold ${order.status === 'paid' ? 'badge-paid' : order.status === 'pending' ? 'badge-pending' : 'badge-cancelled'}`}>
                              {order.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Users tab */}
        {tab === 'users' && (
          <div>
            <div className="flex items-center gap-3 mb-4">
              <input
                type="text"
                value={userSearch}
                onChange={e => setUserSearch(e.target.value)}
                placeholder="Search users by email or name..."
                className="flex-1 max-w-md bg-[#0A0A0A] border border-gray-700 text-white px-3 py-2 rounded text-sm"
                data-testid="user-search-input"
              />
              <select
                value={requestFilter}
                onChange={e => setRequestFilter(e.target.value)}
                className="bg-[#0A0A0A] border border-gray-700 text-white px-3 py-2 rounded text-sm"
                style={{ background: '#0A0A0A' }}
                data-testid="user-request-filter"
              >
                <option value="">All users</option>
                <option value="organiser">Pending organiser requests</option>
                <option value="dj">Pending DJ requests</option>
              </select>
            </div>
            <div className="bg-[#0A0A0A] border border-gray-800 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-gray-500 text-xs uppercase border-b border-gray-800">
                      <th className="text-left p-4">User</th>
                      <th className="text-left p-4">Role</th>
                      <th className="text-left p-4">Status</th>
                      {isSuperAdmin && <th className="text-left p-4">Actions</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-900" data-testid="users-table">
                    {users.map(u => (
                      <tr key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            {u.picture && <img src={u.picture} alt="" className="w-7 h-7 rounded-full" />}
                            <div>
                              <p className="text-white font-medium">{u.name}</p>
                              <p className="text-gray-500 text-xs">{u.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded ${
                            u.role === 'super_admin' ? 'bg-yellow-400/20 text-yellow-400' :
                            u.role === 'organiser' ? 'bg-blue-400/20 text-blue-400' :
                            u.role.includes('admin') ? 'bg-purple-400/20 text-purple-400' :
                            'bg-gray-500/20 text-gray-400'
                          }`}>
                            {u.role?.replace('_', ' ')}
                          </span>
                          {u.account_type === 'dj' && !u.is_dj && (
                            <span className="ml-1.5 text-xs font-bold uppercase px-2 py-0.5 rounded bg-orange-400/20 text-orange-400" data-testid={`dj-request-${u.user_id}`}>
                              DJ request
                            </span>
                          )}
                          {u.account_type === 'organizing' && !(u.capabilities || []).includes('organiser') && (
                            <span className="ml-1.5 text-xs font-bold uppercase px-2 py-0.5 rounded bg-orange-400/20 text-orange-400">
                              Organiser request
                            </span>
                          )}
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {['organiser', 'dj', 'promoter', 'ambassador'].map(cap => {
                              const has = (u.capabilities || []).includes(cap);
                              return isSuperAdmin ? (
                                <button key={cap} onClick={() => handleCapability(u, cap, has ? 'revoke' : 'grant')}
                                  title={has ? `Remove ${cap}` : `Grant ${cap}`}
                                  className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border ${has ? 'border-green-400/40 text-green-400' : 'border-gray-700 text-gray-600 hover:text-gray-300'}`}>
                                  {has ? '✓' : '+'} {cap}
                                </button>
                              ) : has ? (
                                <span key={cap} className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border border-gray-700 text-gray-300">{cap}</span>
                              ) : null;
                            })}
                            <button onClick={() => handleVerify(u)}
                              className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border ${u.verified_at ? 'border-red-500/50 text-red-400' : 'border-gray-700 text-gray-600 hover:text-gray-300'}`}>
                              {u.verified_at ? '✓ verified' : 'verify'}
                            </button>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded ${u.is_banned ? 'badge-cancelled' : 'badge-paid'}`}>
                            {u.is_banned ? 'Banned' : 'Active'}
                          </span>
                        </td>
                        {isSuperAdmin && (
                          <td className="p-4">
                            {/* No banning yourself or another super admin from the UI. */}
                            {u.user_id !== currentUserId && u.role !== 'super_admin' && (
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleBanUser(u.user_id, u.is_banned)}
                                  className={`flex items-center gap-1 text-xs px-2 py-1 rounded border transition-colors ${u.is_banned ? 'text-green-400 border-green-400/30 hover:bg-green-400/10' : 'text-red-400 border-red-400/30 hover:bg-red-400/10'}`}
                                  data-testid={`ban-user-${u.user_id}`}
                                >
                                  {u.is_banned ? <><CheckCircle size={11} /> Unban</> : <><Ban size={11} /> Ban</>}
                                </button>
                                {u.role === 'user' && (
                                  <button
                                    onClick={() => handleToggleDj(u.user_id, u.is_dj)}
                                    className={`flex items-center gap-1 text-xs px-2 py-1 rounded border transition-colors ${u.is_dj ? 'text-orange-400 border-orange-400/30 hover:bg-orange-400/10' : 'text-purple-400 border-purple-400/30 hover:bg-purple-400/10'}`}
                                    data-testid={`toggle-dj-${u.user_id}`}
                                  >
                                    {u.is_dj ? 'Remove DJ' : 'Make DJ'}
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Events tab */}
        {tab === 'events' && (
          <div className="bg-[#0A0A0A] border border-gray-800 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-500 text-xs uppercase border-b border-gray-800">
                    <th className="text-left p-4">Event</th>
                    <th className="text-left p-4">Category</th>
                    <th className="text-left p-4">Date</th>
                    <th className="text-left p-4">Status</th>
                    <th className="text-left p-4">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-900">
                  {events.map(ev => (
                    <tr key={ev.event_id}>
                      <td className="p-4">
                        <p className="text-white font-medium truncate max-w-48">{ev.title}</p>
                        <p className="text-gray-500 text-xs">{ev.city}</p>
                      </td>
                      <td className="p-4 text-gray-400 text-xs uppercase">{ev.category}</td>
                      <td className="p-4 text-gray-400 text-xs">{ev.date}</td>
                      <td className="p-4">
                        <div className="flex gap-1">
                          <span className={`text-xs px-2 py-0.5 rounded font-bold ${ev.is_published ? 'badge-paid' : 'badge-pending'}`}>
                            {ev.is_published ? 'Published' : 'Draft'}
                          </span>
                          {ev.is_featured && <span className="gold-badge text-xs px-1.5 py-0.5 rounded-sm">Featured</span>}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          <button onClick={() => handleTogglePublish(ev)} className={`text-xs px-2 py-1 rounded border transition-colors ${ev.is_published ? 'text-orange-400 border-orange-400/30' : 'text-green-400 border-green-400/30'}`}>
                            {ev.is_published ? <EyeOff size={12} /> : <Eye size={12} />}
                          </button>
                          <button onClick={() => handleToggleFeature(ev)} className={`text-xs px-2 py-1 rounded border transition-colors ${ev.is_featured ? 'text-gray-400 border-gray-700' : 'text-yellow-400 border-yellow-400/30'}`}>
                            <Star size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Audit Logs tab */}
        {tab === 'promoters' && <PromotersLeaderboard />}

        {tab === 'logs' && (
          <div className="bg-[#0A0A0A] border border-gray-800 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-500 text-xs uppercase border-b border-gray-800">
                    <th className="text-left p-4">Admin</th>
                    <th className="text-left p-4">Action</th>
                    <th className="text-left p-4">Target</th>
                    <th className="text-left p-4">Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-900">
                  {logs.map(log => (
                    <tr key={log.log_id}>
                      <td className="p-4 text-gray-400 text-xs">{log.admin_email}</td>
                      <td className="p-4">
                        <span className="text-xs font-mono px-2 py-0.5 rounded bg-gray-800 text-yellow-400">{log.action}</span>
                      </td>
                      <td className="p-4 text-gray-400 text-xs">{log.target_type} {log.target_id?.slice(0,8)}</td>
                      <td className="p-4 text-gray-500 text-xs">{new Date(log.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* FX Management tab */}
        {tab === 'fx' && (
          <div className="max-w-lg">
            <div className="bg-[#0A0A0A] border border-gray-800 rounded-xl p-6">
              <h2 className="text-white font-bold uppercase text-base mb-6">Exchange Rates (Base: EUR)</h2>
              <div className="space-y-4">
                {Object.entries(fxForm).map(([curr, rate]) => (
                  <div key={curr} className="flex items-center gap-3">
                    <span className="text-white font-bold w-12 uppercase">{curr}</span>
                    <input
                      type="number"
                      value={rate}
                      onChange={e => setFxForm(f => ({ ...f, [curr]: parseFloat(e.target.value) || 0 }))}
                      step="0.001"
                      disabled={curr === 'EUR'}
                      className="flex-1 bg-[#111] border border-gray-700 text-white px-3 py-2 rounded text-sm disabled:opacity-50"
                      data-testid={`fx-rate-${curr}`}
                    />
                  </div>
                ))}
              </div>
              <button onClick={handleSaveFX} className="mt-6 btn-primary w-full py-3 rounded text-sm font-bold" data-testid="save-fx-btn">
                Save Rates
              </button>
            </div>
          </div>
        )}

        {/* Payouts tab */}
        {tab === 'payouts' && (
          <div data-testid="admin-payouts-tab">
            <div className="flex items-center gap-3 mb-6">
              <h2 className="text-white font-black uppercase text-lg">Payout Requests</h2>
              <div className="flex gap-1 ml-auto bg-[#0A0A0A] border border-gray-800 rounded-lg p-1">
                {['pending', 'approved', 'rejected', ''].map(s => (
                  <button
                    key={s || 'all'}
                    onClick={() => setPayoutFilter(s)}
                    className={`px-3 py-1.5 rounded text-xs font-bold uppercase transition-colors ${payoutFilter === s ? 'bg-red-600 text-white' : 'text-gray-500 hover:text-white'}`}
                    data-testid={`payout-filter-${s || 'all'}`}
                  >
                    {s || 'All'}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="skeleton h-20 rounded-xl" />)}</div>
            ) : payoutRequests.length === 0 ? (
              <div className="text-center py-16 border border-dashed border-gray-800 rounded-xl">
                <Wallet className="w-10 h-10 text-gray-700 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">No payout requests</p>
              </div>
            ) : (
              <div className="space-y-3">
                {payoutRequests.map(req => (
                  <div key={req.payout_id} className="bg-[#0A0A0A] border border-gray-800 rounded-xl p-5" data-testid={`admin-payout-${req.payout_id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <span className="text-white font-black text-lg">{format(req.amount)}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-bold uppercase ${
                            req.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                            req.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                            'bg-yellow-500/20 text-yellow-400'
                          }`}>{req.status}</span>
                        </div>
                        <p className="text-gray-400 text-sm font-medium">{req.organiser_name} — {req.organiser_email}</p>
                        {req.bank_account && <p className="text-gray-600 text-xs mt-0.5">IBAN: {req.bank_account}</p>}
                        {req.paypal_email && <p className="text-gray-600 text-xs mt-0.5">PayPal: {req.paypal_email}</p>}
                        {req.note && <p className="text-gray-500 text-xs mt-1 italic">"{req.note}"</p>}
                        <p className="text-gray-700 text-xs mt-1">{req.created_at?.slice(0, 10)}</p>
                      </div>
                      {req.status === 'pending' && isSuperAdmin && (
                        <div className="flex gap-2 flex-shrink-0">
                          <button
                            onClick={async () => {
                              try {
                                await payoutAPI.approve(req.payout_id);
                                setPayoutRequests(p => p.map(r => r.payout_id === req.payout_id ? { ...r, status: 'approved' } : r));
                                toast.success('Payout approved');
                              } catch { toast.error('Failed'); }
                            }}
                            className="flex items-center gap-1 px-3 py-2 bg-green-600/20 hover:bg-green-600/30 border border-green-600/30 text-green-400 text-xs font-bold rounded transition-colors"
                            data-testid={`approve-payout-${req.payout_id}`}
                          >
                            <CheckCircle size={12} /> {t('approve')}
                          </button>
                          <button
                            onClick={async () => {
                              try {
                                await payoutAPI.reject(req.payout_id, 'Rejected by admin');
                                setPayoutRequests(p => p.map(r => r.payout_id === req.payout_id ? { ...r, status: 'rejected' } : r));
                                toast.success('Payout rejected');
                              } catch { toast.error('Failed'); }
                            }}
                            className="flex items-center gap-1 px-3 py-2 bg-red-600/20 hover:bg-red-600/30 border border-red-600/30 text-red-400 text-xs font-bold rounded transition-colors"
                            data-testid={`reject-payout-${req.payout_id}`}
                          >
                            <XCircle size={12} /> {t('reject')}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Admin Modal */}
      {showCreateAdmin && isSuperAdmin && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="create-admin-modal">
          <div className="bg-[#0A0A0A] border border-gray-700 rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-white font-black uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>Create Admin</h3>
              <button onClick={() => setShowCreateAdmin(false)} className="text-gray-500 hover:text-white"><X size={20} /></button>
            </div>
            <form onSubmit={handleCreateAdmin} className="space-y-4">
              <div>
                <label className="text-gray-400 text-xs font-medium uppercase mb-1.5 block">Email</label>
                <input required type="email" value={adminForm.email} onChange={e => setAdminForm(f => ({ ...f, email: e.target.value }))} className="w-full bg-[#111] border border-gray-700 text-white px-3 py-2.5 rounded text-sm" data-testid="admin-email-input" />
              </div>
              <div>
                <label className="text-gray-400 text-xs font-medium uppercase mb-1.5 block">Name</label>
                <input type="text" value={adminForm.name} onChange={e => setAdminForm(f => ({ ...f, name: e.target.value }))} className="w-full bg-[#111] border border-gray-700 text-white px-3 py-2.5 rounded text-sm" />
              </div>
              <div>
                <label className="text-gray-400 text-xs font-medium uppercase mb-1.5 block">Role</label>
                <select value={adminForm.role} onChange={e => setAdminForm(f => ({ ...f, role: e.target.value }))} className="w-full bg-[#111] border border-gray-700 text-white px-3 py-2.5 rounded text-sm" style={{ background: '#111' }} data-testid="admin-role-select">
                  {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value} style={{ background: '#111' }}>{r.label}</option>)}
                </select>
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowCreateAdmin(false)} className="flex-1 py-2.5 border border-gray-700 rounded text-sm text-gray-400">Cancel</button>
                <button type="submit" className="flex-1 btn-primary py-2.5 rounded text-sm font-bold" data-testid="create-admin-submit-btn">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Platform-wide promoter & brand-ambassador leaderboard. SAC Events campaigns
 * are created by opening any event's Team & lineup page → Promoters.
 */
function PromotersLeaderboard() {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    adminRolesAPI.promoters().then(r => setRows(r.data)).catch(() => setRows([]));
  }, []);

  if (!rows) return <p className="text-gray-400">Loading…</p>;

  return (
    <div className="bg-[#0A0A0A] border border-gray-800 rounded-xl overflow-hidden">
      <p className="text-gray-500 text-xs p-4 border-b border-gray-800">
        To run a SAC Events campaign, open an event → Team &amp; lineup → Promoters and assign an ambassador.
        Grant the Promoter or Ambassador role from the Users tab.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 text-xs uppercase">
              {['#', 'Promoter', 'Campaigns', 'Clicks', 'Tickets', 'Revenue', 'Earned'].map(h => <th key={h} className="p-4">{h}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-900">
            {rows.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-gray-500">No promoters yet.</td></tr>}
            {rows.map((r, i) => (
              <tr key={r.user_id}>
                <td className="p-4 text-gray-500">{i + 1}</td>
                <td className="p-4">
                  <span className="text-white font-medium">{r.name}</span>
                  <span className={`ml-2 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${r.is_ambassador ? 'bg-yellow-400/20 text-yellow-400' : 'bg-red-400/20 text-red-400'}`}>
                    {r.is_ambassador ? 'Ambassador' : 'Promoter'}
                  </span>
                </td>
                <td className="p-4 text-gray-300">{r.campaigns}</td>
                <td className="p-4 text-gray-300">{r.clicks}</td>
                <td className="p-4 text-white font-bold">{r.tickets}</td>
                <td className="p-4 text-gray-300">€{Number(r.revenue).toFixed(2)}</td>
                <td className="p-4 text-gray-300">€{Number(r.earnings).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
